import CurrencyFormatter from '../../utils/currency-formatter-lib';

export default class Pricing {
    constructor(parent) {
        this.parent = parent;
        this.client = parent.client;
        this.data = parent.data;
        this.car = parent.car;
        this.mtoPricing = {};
        this.mtocePricing = {};
        this.formatter = new CurrencyFormatter();
    }

    format(val, allowZero, el) {
        const value = val !== null && !isNaN(val) ? Number(val) : NaN;

        if(isNaN(value) || value < 0){
            return el?.dataset?.missingPrice ||
                window.HONDA.CarConfigurator.labels.missingPrice;
        }

        if (!allowZero && value === 0) {
            return el?.dataset?.asStandard ||
                window.HONDA.CarConfigurator.labels.asStandard;
        }

        return this.formatter.to(value);
    }

    async getMTOPricing() {
        return null;
    }

    async waitForMTOPricing() {
        return null;
    }

    async getMTOCEPricing() {
        return null;
    }

    async waitForMTOCEPricing() {
        return null;
    }

    hasPricing() {
        return true;
    }

    getPricingBreakdown() {
        return [];
    }

    getMTOPrices({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        return this.data.cars.reduce((map, car) => {
            const mto = car.mto;
            const key = 'price';
            const price = car[key];
            if (mto && price != null && !isNaN(price)) {
                map[mto] = Number(price);
            }
            return map;
        }, {});
    }

    getColourPricesMTO() {
        const data = this.car.data();
        if (data && data.colourTrims) {
            return data.colourTrims.reduce((map, colourTrim) => {
                const colour = colourTrim.colourMTOCE;
                const price = colourTrim.colourPrice;
                if (colour && price != null && !isNaN(price)) {
                    map[colour] = Number(price);
                }
                return map;
            }, {});
        }
        return {};
    }

    getColourPrices() {
        return this.data.cars
            .flatMap((p) => p.colourTrims)
            .filter((e) => e)
            .reduce((map, colourTrim) => {
                const colour = colourTrim.colourMTOCE;
                const price = colourTrim.colourPrice;
                if (map[colour] == null) {
                    map[colour] = price ? Number(price) : null;
                }
                return map;
            }, this.getColourPricesMTO()) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getTrimPricesMTO() {
        const data = this.car.data();
        if (data && data.colourTrims) {
            return data.colourTrims.reduce((map, colourTrim) => {
                const trim = colourTrim.trimMTOCE;
                const price = colourTrim.trimPrice;
                if (trim && price != null && !isNaN(price)) {
                    map[trim] = Number(price);
                }
                return map;
            }, {});
        }
        return {};
    }

    getTrimPrices() {
        return this.data.cars
            .flatMap((car) => car.colourTrims)
            .filter((ct) => ct)
            .reduce((map, colourTrim) => {
                const trim = colourTrim.trimMTOCE;
                const price = colourTrim.trimPrice;
                if (map[trim] == null) {
                    map[trim] = price ? Number(price) : null;
                }
                return map;
            }, this.getTrimPricesMTO()) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getColourTrimPricesMTO({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        const data = this.car.data();
        if (data && data.colourTrims) {
            return data.colourTrims.reduce((map, colourTrim) => {
                const ct = `${colourTrim.colourMTOCE}|${colourTrim.trimMTOCE}`;
                const price = colourTrim.price;
                if (ct && price != null && !isNaN(price)) {
                    map[ct] = Number(price);
                }
                return map;
            }, {});
        }
        return {};
    }

    getColourTrimPrices({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        return this.data.cars
            .flatMap((car) => car.colourTrims)
            .filter((ct) => ct)
            .reduce((map, colourTrim) => {
                const ct = `${colourTrim.colourMTOCE}|${colourTrim.trimMTOCE}`;
                const key = 'price';
                const price = colourTrim[key];
                if(map[ct] == null && !discountPrice) {
                    map[ct] = (price != null  && !isNaN(price)) ? Number(price) : null;
                }
                return map;
            }, this.getColourTrimPricesMTO({ discountPrice })) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getAccessoryPricesMTO({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        return this.getAccessoryPrices({ discountPrice });
    }

    getAccessoryPrices({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        return Object.values(this.data.accessories)
            .reduce((map, accessory) => {
                const key = 'fittedInclVat';
                const price = accessory[key];
                if(accessory.mtoce && map[accessory.mtoce] == null) {
                    map[accessory.mtoce] = (price != null  && !isNaN(price)) ? Number(price) : null;
                }
                return map;
            }, {}) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getWheelPrices({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        const defaultPrice = 0.0;
        const data = this.getAccessoryPrices({ discountPrice });
        return new Proxy(data, {
            get(obj, p) {
                const fallback = discountPrice ? null : defaultPrice;
                return obj.hasOwnProperty(p) ? obj[p] : fallback;
            },
        });
    }

    getAdditionalProductsMTO(mto) {
        const data = this.data.additionalProducts;
        if(data) {
            return data.map((ap) => this.parseAdditionalProductDependencies(ap));
        }
        return [];
    }

    getAdditionalProductMTO(ean, mto) {
        const additionalProducts = this.data.additionalProducts;
        return additionalProducts?.find((ap) => ap.code === ean);
    }

    getAdditionalProductPricesMTO({ discountPrice } = {}) {
        if(discountPrice) { return {}; }
        const data = this.data.additionalProducts;
        if(data) {
            return data.reduce((map, product) => {
              const key = 'fallbackPrice';
              const price = product[key];
              map[product.code] = (price != null  && !isNaN(price)) ? Number(price) : null;
              return map;
            }, {}) ?? {};
        }
        return {};
    }

    getCarCapCode() {
        return this.car.data()?.financeCode ?? null;
    }

    getCarHoldingFee() {
        return 0;
    }

    getCarMTOPricing(mto) {
        return null;
    }

    getCarMTOCEPricing() {
        return null;
    }

    getCarAdditionalProducts({ tempCar, financeableAdditionalProducts, optionalAdditionalProducts } = {}) {
        const car = tempCar ?? this.car;
        return car.additionalProducts
            .map((product) => this.data.additionalProducts.find((p) => p.code === product))
            .filter((p) => p)
            .map((p) => this.parseAdditionalProductDependencies(p));
    }

    parseAdditionalProductDependencies(product) {
        product.cantHave = product.fallbackIncompatible?.split(',')
            ?.map((s) => s.trim()) ?? [];
        product.mustHave = product.fallbackMustHave?.split(',')
            ?.map((s) => s.trim()) ?? [];
        return product;
    }

    getCarColourTrim() {
        return this.car.colourTrim();
    }

    getCarBundle({ type } = {}) {
        return null;
    }

    getCarBreakdownPrice({ type } = {}) {
        return null;
    }

    getCarBundlePrice({ type, defaultVal, discountPrice } = {}) {
        return null;
    }

    getCarBreakdownColourPrice() {
        return null;
    }

    getCarBreakdownTrimPrice() {
        return null;
    }

    getCarColourTrimPrice({ discountPrice } = {}) {
        if(discountPrice) { return null; }
        return Number(this.car.colourTrim().price ?? NaN);
    }

    getCarAccessoriesPrice({ discountPrice } = {}) {
        if(discountPrice) { return null; }
        const accessoryPrices = this.getAccessoryPrices({ discountPrice });
        const accessories = this.car.accessories
            .map((acc) => this.data.accessories[acc])
            .map((acc) => accessoryPrices[acc.mtoce] ?? 0);
        const totalPrice = accessories.reduce((total, curr) => total + (curr ?? NaN), 0);
        return !isNaN(totalPrice) ? totalPrice : null;
    }

    getCarAdditionalProductsPrice({ financeableAdditionalProducts, discountPrice, optionalAdditionalProducts } = {}) {
        if(discountPrice) { return null; }
        const additionalProductPrices = this.getAdditionalProductPricesMTO({ discountPrice });
        const additionalProducts = this.getCarAdditionalProducts({ financeableAdditionalProducts, optionalAdditionalProducts });
        const prices = additionalProducts.map((ap) => additionalProductPrices[ap.code] ?? 0);
        const totalPrice = prices.reduce((total, curr) => total + (curr ?? NaN), 0);
        return !isNaN(totalPrice) ? totalPrice : null;
    }

    getCarBasePrice({ financeableAdditionalProducts, discountPrice } = {}) {
        if(discountPrice) { return null; }
        const price = this.car.data()?.price;
        return price != null ? Number(price) : null;
    }

    getCarTotalPrice({ financeableAdditionalProducts, discountPrice } = {}) {
        if(discountPrice) { return null; }
        const basePrice = this.getCarBasePrice({ financeableAdditionalProducts, discountPrice });
        const colourTrimPrice = this.getCarColourTrimPrice({ discountPrice });
        const accessoriesPrice = this.getCarAccessoriesPrice({ discountPrice });
        const additionalProductsPrice = this.getCarAdditionalProductsPrice({ financeableAdditionalProducts, discountPrice });
        const totalPrice = [basePrice, colourTrimPrice, accessoriesPrice, additionalProductsPrice].reduce((total, curr) => total + (curr ?? NaN), 0);
        return !isNaN(totalPrice) ? totalPrice : null;
    }
}
